import { DataService } from './data-service';
import { broadcastProjectsChanged } from './project-changes-channel';
import { createWorkOrderFromProject, buildPhases } from './work-order-service';
import { runWorkOrderDraftRouted } from './work-order-draft-routed';

const projAddress = (proj: any) => proj?.formData?.address || proj?.address || 'project';

export interface ProjectOutcomeContext {
    currentProjectId: string | null;
    projects: any[];
    setProjects: React.Dispatch<React.SetStateAction<any[]>>;
    adoptServerProjectVersion: (projectId: string, persisted: any) => void;
    setToast: (opts: any) => void;
    apiKey: string | null;
    model: string;
}

export async function executeCostingsSent(
    info: any,
    ctx: ProjectOutcomeContext
): Promise<void> {
    const { to, at } = info || {};
    if (!ctx.currentProjectId) return;
    const projectId = ctx.currentProjectId;
    try {
        const summary = ctx.projects.find(p => p.id === projectId) || {};
        const persisted = await DataService.projects.recordCostingsSent(projectId, { to, clientAt: at || null });
        const sentAt = persisted?.costingsSentAt || new Date().toISOString();
        const updatedAt = persisted?.updatedAt || sentAt;
        const patch = {
            costingsSent: true,
            costingsSentAt: sentAt,
            costingsSentTo: to || '',
            updatedAt,
            updated_at: updatedAt,
            aiContext: { ...(summary.aiContext || {}), ...(persisted?.aiContext || {}) },
        };
        ctx.setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...patch } : p));
        broadcastProjectsChanged({ kind: 'misc', projectIds: [projectId] });
        const projForLog = { ...summary, ...persisted, ...patch };
        try {
            await DataService.crmActivities.log({
                company_id: projForLog?.companyId || projForLog?.company_id || null,
                project_id: projectId,
                activity_type: 'costings_sent',
                description: `Costings sent to ${to || 'admin'} for ${projAddress(projForLog)}`,
            });
        } catch (e: any) { console.warn('[QM] CRM activity log (costings_sent) failed:', e?.message); }
        ctx.setToast({ message: `Costings logged — sent to ${to || 'admin'}`, type: 'success' });
    } catch (err) {
        console.error('[QM] handleCostingsSent failed:', err);
        ctx.setToast({ message: 'Failed to log costings', type: 'error' });
    }
}

export async function executeMarkWon(
    ctx: ProjectOutcomeContext
): Promise<void> {
    if (!ctx.currentProjectId) return;
    const projectId = ctx.currentProjectId;
    try {
        const summary = ctx.projects.find(p => p.id === projectId) || {};
        const persisted = await DataService.projects.recordOutcome(projectId, { status: 'won' });
        ctx.adoptServerProjectVersion(projectId, persisted);
        const now = persisted?.outcomeAt || persisted?.wonAt || new Date().toISOString();
        const updatedAt = persisted?.updatedAt || now;
        ctx.setProjects(prev => prev.map(p => p.id === projectId ? {
            ...p,
            status: 'won',
            quoteStatus: 'won',
            wonAt: now,
            lostAt: null,
            lostReason: null,
            updatedAt,
            updated_at: updatedAt,
            aiContext: { ...(p.aiContext || {}), ...(persisted?.aiContext || {}) },
        } : p));
        broadcastProjectsChanged({ kind: 'misc', projectIds: [projectId] });
        await onProjectWonBackground({
            projectId,
            summary,
            persisted,
            now,
            apiKey: ctx.apiKey,
            model: ctx.model,
            setToast: ctx.setToast,
        });
    } catch (err) {
        console.error('[QM] handleMarkWon failed:', err);
        ctx.setToast({ message: 'Failed to mark as won', type: 'error' });
    }
}

export async function onProjectWonBackground(opts: {
    projectId: string;
    summary: any;
    persisted: any;
    now: string;
    apiKey: string | null;
    model: string;
    setToast: (opts: any) => void;
}): Promise<void> {
    const { projectId, summary, persisted, now, setToast } = opts;
    let projForLog = { ...summary, ...persisted, status: 'won', quoteStatus: 'won', wonAt: now };
    try {
        projForLog = await DataService.projects.getById(projectId) || projForLog;
    } catch (e: any) {
        console.warn('[QM] Full project refresh after WON failed (non-fatal):', e?.message);
    }
    try {
        await DataService.crmActivities.log({
            company_id: projForLog?.companyId || projForLog?.company_id || null,
            project_id: projectId,
            activity_type: 'status_change',
            description: `Marked WON — ${projAddress(projForLog)}`,
        });
    } catch (e: any) { console.warn('[QM] CRM activity log (won) failed:', e?.message); }

    try {
        if (projForLog?.scope?.length > 0) {
            const woId = await createWorkOrderFromProject(projForLog);
            console.log(`[QM] Pre-execution audit complete: work order ${woId} created for project ${projectId}`);
            setToast({ message: `Marked as WON - Work Order draft generated`, type: 'success' });

            try {
                const phases = buildPhases(projForLog.scope);
                const phasesWithItems = phases.map((p: any) => ({
                    ...p,
                    items: p.scopeItemIds
                        .map((id: any) => projForLog.scope.find((s: any) => s.id === id))
                        .filter(Boolean)
                        .map((it: any) => ({
                            id: it.id,
                            title: it.title,
                            description: it.description,
                            amount: it.amount,
                        })),
                }));
                const wodResult: any = await runWorkOrderDraftRouted({
                    projectId,
                    projectTitle: projForLog.formData?.address || projForLog.title || 'Untitled Project',
                    phases: phasesWithItems,
                    overallNotes: projForLog.executiveSummary || '',
                    wmsSummary: projForLog.wms?.summary || '',
                });
                if (wodResult.queued) {
                    console.log(`[QM] AI work-order draft queued — jobId=${wodResult.jobId}`);
                } else if (wodResult.reason && wodResult.reason !== 'inline_path') {
                    console.warn(`[QM] AI work-order draft not queued — reason=${wodResult.reason}`);
                }
            } catch (wodErr: any) {
                console.warn('[QM] AI work-order draft dispatch failed (non-fatal):', wodErr?.message);
            }
        } else {
            setToast({ message: `Marked as WON`, type: 'success' });
        }
    } catch (woErr: any) {
        console.warn('[QM] Work order generation failed (non-fatal):', woErr?.message);
        setToast({ message: `Marked as WON`, type: 'success' });
    }
}

export async function executeMarkLost(
    reason: any,
    ctx: ProjectOutcomeContext
): Promise<void> {
    if (!ctx.currentProjectId) return;
    const projectId = ctx.currentProjectId;
    try {
        const summary = ctx.projects.find(p => p.id === projectId) || {};
        const persisted = await DataService.projects.recordOutcome(projectId, { status: 'lost', reason });
        ctx.adoptServerProjectVersion(projectId, persisted);
        const now = persisted?.outcomeAt || persisted?.lostAt || new Date().toISOString();
        const updatedAt = persisted?.updatedAt || now;
        ctx.setProjects(prev => prev.map(p => p.id === projectId ? {
            ...p,
            status: 'lost',
            quoteStatus: 'lost',
            lostAt: now,
            wonAt: null,
            lostReason: reason || '',
            updatedAt,
            updated_at: updatedAt,
            aiContext: { ...(p.aiContext || {}), ...(persisted?.aiContext || {}) },
        } : p));
        broadcastProjectsChanged({ kind: 'misc', projectIds: [projectId] });
        await onProjectLostBackground({
            projectId,
            reason,
            summary,
            persisted,
            now,
            apiKey: ctx.apiKey,
            model: ctx.model,
            setToast: ctx.setToast,
        });
    } catch (err) {
        console.error('[QM] handleMarkLost failed:', err);
        ctx.setToast({ message: 'Failed to mark as lost', type: 'error' });
    }
}

export async function onProjectLostBackground(opts: {
    projectId: string;
    reason: any;
    summary: any;
    persisted: any;
    now: string;
    apiKey: string | null;
    model: string;
    setToast: (opts: any) => void;
}): Promise<void> {
    const { projectId, reason, summary, persisted, now, setToast } = opts;
    let projForLog = { ...summary, ...persisted, status: 'lost', quoteStatus: 'lost', lostAt: now, lostReason: reason || '' };
    try {
        projForLog = await DataService.projects.getById(projectId) || projForLog;
    } catch (e: any) {
        console.warn('[QM] Full project refresh after LOST failed (non-fatal):', e?.message);
    }
    try {
        await DataService.crmActivities.log({
            company_id: projForLog?.companyId || projForLog?.company_id || null,
            project_id: projectId,
            activity_type: 'status_change',
            description: `Marked LOST — ${projAddress(projForLog)}${reason ? ` (${reason})` : ''}`,
        });
    } catch (e: any) { console.warn('[QM] CRM activity log (lost) failed:', e?.message); }

    setToast({ message: `Marked as Lost`, type: 'info' });
}

export async function executeSetFollowUp(
    dueDate: any,
    note: any,
    ctx: ProjectOutcomeContext
): Promise<void> {
    if (!ctx.currentProjectId) return;
    if (!dueDate) return;
    const projectId = ctx.currentProjectId;
    try {
        const summary = ctx.projects.find(p => p.id === projectId) || {};
        const persisted = await DataService.projects.recordFollowUp(projectId, { dueDate, note });
        ctx.adoptServerProjectVersion(projectId, persisted);
        const updatedAt = persisted?.updatedAt || new Date().toISOString();
        const patch = {
            nextFollowUp: dueDate,
            nextFollowUpNote: note || '',
            updatedAt,
            updated_at: updatedAt,
            aiContext: { ...(summary.aiContext || {}), ...(persisted?.aiContext || {}) },
        };
        ctx.setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...patch } : p));
        broadcastProjectsChanged({ kind: 'misc', projectIds: [projectId] });
        const projForLog = { ...summary, ...persisted, ...patch };
        try {
            await DataService.crmActivities.log({
                company_id: projForLog?.companyId || projForLog?.company_id || null,
                project_id: projectId,
                activity_type: 'follow_up',
                description: note || `Follow up on quote for ${projAddress(projForLog)}`,
                due_date: dueDate,
            });
        } catch (e: any) { console.warn('[QM] CRM activity log (follow_up) failed:', e?.message); }
        ctx.setToast({ message: `Follow-up scheduled for ${new Date(dueDate).toLocaleDateString('en-AU')}`, type: 'success' });
    } catch (err: any) {
        console.warn('[QM] handleSetFollowUp failed:', err?.message);
        ctx.setToast({ message: 'Failed to schedule follow-up', type: 'error' });
    }
}
